/**
 * PERSONA PREFERENCES — pins, default and recent, through the governed door.
 *
 * ── the storage split, and it is a RULING ──────────────────────────────────
 * ROOT ruling 74071 §7: *"Active persona remains tab-local/session-scoped. Pins,
 * default and recent persona preferences use the governed personal-settings
 * door."* The two halves are different kinds of fact and that is why they live
 * in different places:
 *
 *   · the ACTIVE persona is a statement about THIS TAB — which experience I am
 *     looking at right now. `app/persona.ts` keeps it in `sessionStorage`, whose
 *     lifetime IS the tab, and that decision is argued there and pinned by a
 *     two-tab browser spec. Nothing here moves it.
 *   · a PIN, a DEFAULT and a RECENT are statements about THE PERSON — they must
 *     follow them to another device and must never follow them to another
 *     account. `core.user_setting` is keyed by authenticated user, so both
 *     properties come from the door rather than from a discipline this file
 *     would have to keep.
 *
 * So `localStorage` is not a fallback here, it is the defect: it is per ORIGIN,
 * so a second user on the same browser would inherit the first user's pins, and
 * a person on a second device would inherit nothing. #2328 §2 states both.
 *
 * ── ONE round trip, not three ─────────────────────────────────────────────
 * The same `['user-settings']` query key as `pins.ts` and `recentRecords.ts`,
 * because `my_settings()` returns the whole per-user map in one read and
 * react-query dedupes on the key. A second key would be a second poll of the
 * same door.
 *
 * ── never write over an UNREAD base ───────────────────────────────────────
 * `pins.ts`'s rule, and the reason is identical: writing a computed value over
 * a map that was never read erases whatever else was in it. `q.isSuccess` is the
 * only state in which the base is known; skipping a write loses one click, and
 * the alternative loses the person's workspace.
 */
import { useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSolum } from '../data/client'

/** The one `core.user_setting` key this file owns. */
export const PERSONA_PREFS_KEY = 'persona.prefs'

/** How many recently-used personas are remembered. A menu section, not a log. */
export const MAX_RECENT_PERSONAS = 5

export interface PersonaPrefs {
  /** Pinned persona keys, in the order they were pinned. */
  pinned: string[]
  /** Recently activated persona keys, most recent first. */
  recent: string[]
  /** The persona this person prefers to start in, where they have chosen one. */
  defaultKey: string | null
}

const EMPTY: PersonaPrefs = { pinned: [], recent: [], defaultKey: null }

/** Defensive read of the free-jsonb preference value. Unknown shapes read as empty. */
export function personaPrefsFromSettings(settings: unknown): PersonaPrefs {
  const raw = (settings as Record<string, unknown> | null | undefined)?.[PERSONA_PREFS_KEY]
  if (typeof raw !== 'object' || raw === null) return EMPTY
  const row = raw as Partial<Record<keyof PersonaPrefs, unknown>>
  const keys = (value: unknown): string[] => Array.isArray(value)
    ? [...new Set(value.filter((k): k is string => typeof k === 'string' && k !== ''))]
    : []
  const defaultKey = typeof row.defaultKey === 'string' && row.defaultKey !== ''
    ? row.defaultKey : null
  return {
    pinned: keys(row.pinned),
    recent: keys(row.recent).slice(0, MAX_RECENT_PERSONAS),
    defaultKey,
  }
}

/** Pin or unpin, without reordering the rest. */
export function togglePersonaPin(prefs: PersonaPrefs, key: string): PersonaPrefs {
  return prefs.pinned.includes(key)
    ? { ...prefs, pinned: prefs.pinned.filter((k) => k !== key) }
    : { ...prefs, pinned: [...prefs.pinned, key] }
}

/** Activating a persona moves it to the top of recent without duplicating it. */
export function touchRecentPersona(prefs: PersonaPrefs, key: string): PersonaPrefs {
  if (key === '') return prefs
  return {
    ...prefs,
    recent: [key, ...prefs.recent.filter((k) => k !== key)].slice(0, MAX_RECENT_PERSONAS),
  }
}

export interface PersonaPrefsController extends PersonaPrefs {
  /** False means the governed base was unread, so no write was attempted. */
  ready: boolean
  /** Read settlement is separate from write readiness: a refused preference
   * read may still fall back to the caller's governed persona assignment. */
  readState: 'loading' | 'ready' | 'refused'
  pin: (key: string) => void
  unpin: (key: string) => void
  setDefault: (key: string) => void
  remember: (key: string) => void
}

export function usePersonaPrefs(): PersonaPrefsController {
  const { client } = useSolum()
  const qc = useQueryClient()

  const q = useQuery({
    queryKey: ['user-settings'],
    queryFn: () => client.raw().rpcScalar<Record<string, unknown>>('my_settings'),
    staleTime: 60_000,
  })
  const prefs = useMemo(() => personaPrefsFromSettings(q.data), [q.data])

  const save = useMutation({
    /* Serial, like `recentRecords.ts`: each queued write computes from the cache
       the preceding success left, so pinning twice quickly cannot drop one. */
    scope: { id: 'persona-prefs' },
    mutationFn: async (next: PersonaPrefs) => {
      await client.raw().rpcScalar(
        'set_user_setting', { p_key: PERSONA_PREFS_KEY, p_value: next },
      )
      return next
    },
    onSuccess: (next) => {
      qc.setQueryData<Record<string, unknown>>(['user-settings'], (current) => ({
        ...(current ?? {}), [PERSONA_PREFS_KEY]: next,
      }))
      void qc.invalidateQueries({ queryKey: ['user-settings'] })
    },
  })

  const write = useCallback((compute: (base: PersonaPrefs) => PersonaPrefs) => {
    if (!q.isSuccess) return
    const latest = qc.getQueryData<Record<string, unknown>>(['user-settings']) ?? q.data
    save.mutate(compute(personaPrefsFromSettings(latest)))
  }, [q.isSuccess, q.data, qc, save])

  return {
    ...prefs,
    ready: q.isSuccess,
    readState: q.isSuccess ? 'ready' : q.isError ? 'refused' : 'loading',
    pin: useCallback((key: string) => {
      write((base) => (base.pinned.includes(key) ? base : togglePersonaPin(base, key)))
    }, [write]),
    unpin: useCallback((key: string) => {
      write((base) => (base.pinned.includes(key) ? togglePersonaPin(base, key) : base))
    }, [write]),
    setDefault: useCallback((key: string) => {
      write((base) => ({ ...base, defaultKey: key }))
    }, [write]),
    remember: useCallback((key: string) => {
      write((base) => touchRecentPersona(base, key))
    }, [write]),
  }
}
